import { parseBooleanVal } from "Utils/utils";
import { getBreakdownCataloguePrice } from "./codeAllocationRates";

export const mapBreakdownItemNumbersToOptions = (items) => {
  if (!Array.isArray(items) || items.length === 0) return [];
  return items.map((el) => ({
    label: `${el.support_item_name} - ${el?.support_item_number}`,
    value: el.id,
    price: el.price,
    number: el?.support_item_number,
  }));
};

/** Ensure selected item (often custom:uuid) exists in dropdown options. */
export const ensureSelectedItemNumberOption = (options = [], selected) => {
  const list = Array.isArray(options) ? [...options] : [];
  if (!selected || selected.id == null) return list;
  if (list.some((opt) => String(opt?.value) === String(selected.id))) {
    return list;
  }
  list.unshift({
    label: selected?.support_item_name
      ? `${selected.support_item_name} - ${selected?.support_item_number ?? ""}`.trim()
      : String(selected.id),
    value: selected.id,
    price: selected.price,
    number: selected?.support_item_number,
  });
  return list;
};

/** Stable string key for item-number option lists (avoids effect loops on new array refs). */
export const getItemNumberOptionsSignature = (options = []) =>
  (options ?? [])
    .map((o) => `${o?.value ?? ""}:${o?.price ?? ""}:${o?.number ?? ""}`)
    .join("|");

/** PostgreSQL signed integer max — catalogue ids above this are unique_codes. */
export const POSTGRES_INTEGER_MAX = 2147483647;

export const isPostgresIntegerId = (value) => {
  if (value == null || value === "") return false;
  if (typeof value === "string" && value.startsWith("custom:")) return false;
  const num = Number(value);
  return Number.isInteger(num) && num > 0 && num <= POSTGRES_INTEGER_MAX;
};

export const resolveBreakdownInvoiceItemId = (item) => {
  if (!item) return null;
  const selected = item?.item_numbers?.selected;
  const selectedId =
    selected && typeof selected === "object" ? selected.id : null;
  const customUuid =
    item?.custom_line_item_uuid || item?.custom_line_item?.uuid || null;

  if (customUuid) {
    return `custom:${customUuid}`;
  }

  if (
    selectedId != null &&
    typeof selectedId === "string" &&
    selectedId.startsWith("custom:")
  ) {
    return selectedId;
  }

  // Saved catalogue id wins when it fits the integer column.
  if (isPostgresIntegerId(item?.invoice_item_id)) {
    return item.invoice_item_id;
  }

  // Custom lines store null invoice_item_id — use selected `custom:uuid`.
  if (
    selectedId != null &&
    (typeof selectedId === "string" || Number(selectedId) > 0)
  ) {
    return selectedId;
  }
  return null;
};

export const resolveBreakdownItemOption = (item, itemNumberOptions = []) => {
  const itemId = resolveBreakdownInvoiceItemId(item);
  if (!itemId) return null;

  const fromOptions = itemNumberOptions.find(
    (opt) => String(opt.value) === String(itemId)
  );
  if (fromOptions) return fromOptions;

  const selected = item?.item_numbers?.selected;
  if (selected && typeof selected === "object" && selected.id != null) {
    return {
      label: `${selected.support_item_name ?? ""} - ${
        selected.support_item_number ?? ""
      }`.trim(),
      value: selected.id,
      price: selected.price,
      number: selected.support_item_number,
    };
  }

  if (item?.ndis_support_item_number) {
    return {
      label: item?.ndis_support_item_name ?? item.ndis_support_item_number,
      value: itemId,
      price: item?.invoice_price ?? item?.ndis_rate ?? null,
      number: item.ndis_support_item_number,
    };
  }

  return null;
};

export const resolveBreakdownDisplayItemNumber = (item, selectedOption) =>
  selectedOption?.number ??
  item?.ndis_support_item_number ??
  item?.item_numbers?.selected?.support_item_number ??
  "";

export const breakdownItemUsesAlternateRate = (item, cataloguePrice) => {
  if (!item) return false;
  const flag = item.alternate_rate;
  // Explicit false/0 must win before parseBooleanVal (false → false is fine, but
  // keep the ordered checks so catalogue inference only runs when flag is unknown).
  if (flag === false || flag === 0 || flag === "0") {
    return false;
  }
  const isExplicitAlternate =
    flag === true ||
    flag === 1 ||
    flag === "1" ||
    parseBooleanVal(flag);
  if (isExplicitAlternate) return true;
  if (cataloguePrice == null || cataloguePrice === "") return false;
  const saved = Number(item?.ndis_rate ?? item?.invoice_price);
  const catalogue = Number(cataloguePrice);
  return (
    !Number.isNaN(saved) &&
    !Number.isNaN(catalogue) &&
    saved !== catalogue
  );
};

const toFiniteNumberOrNull = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  if (value === "alternate") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

/**
 * Unit rate for hydrate/merge from a persisted billing line.
 * Mixed alternate/actual: untouched "actual" rows often save as null/0 because
 * RHF never received invoice_ndis_rate_* (UI showed catalogue from item only).
 * Prefer catalogue selected.price over a bogus zero for non-alternate lines.
 */
export const resolvePersistedBreakdownUnitRate = (row) => {
  if (!row) return null;

  const isAlternate = parseBooleanVal(row?.alternate_rate);
  const savedNum = toFiniteNumberOrNull(row?.ndis_rate ?? row?.invoice_price);
  const catalogueNum = toFiniteNumberOrNull(
    row?.item_numbers?.selected?.price ?? row?.catalogue_price
  );

  if (isAlternate) {
    if (savedNum != null) return savedNum;
    return catalogueNum;
  }

  if (
    savedNum != null &&
    !(savedNum === 0 && catalogueNum != null && catalogueNum > 0)
  ) {
    return savedNum;
  }
  if (catalogueNum != null) return catalogueNum;
  return savedNum;
};

/**
 * Unit rate for code-allocation submit. Falls back to row/catalogue when the
 * segment was never touched (common for "actual" rows next to alternate ones).
 */
export const resolveSubmitBreakdownUnitRate = ({
  invoiceNdisRate,
  rateAmount,
  breakdownRow,
} = {}) => {
  if (invoiceNdisRate === "alternate") {
    const fromAmount = toFiniteNumberOrNull(rateAmount);
    if (fromAmount != null) return fromAmount;
    return resolvePersistedBreakdownUnitRate({
      ...breakdownRow,
      alternate_rate: true,
    });
  }

  const fromHourly = toFiniteNumberOrNull(invoiceNdisRate);
  if (fromHourly != null) return fromHourly;

  const fromAmount = toFiniteNumberOrNull(rateAmount);
  if (fromAmount != null) return fromAmount;

  const fallback = resolvePersistedBreakdownUnitRate({
    ...breakdownRow,
    alternate_rate: false,
  });
  if (fallback != null) return fallback;

  if (fromHourly === 0) return 0;
  if (fromAmount === 0) return 0;
  return null;
};

export const getBreakdownSegmentHydrationKey = (
  item,
  index,
  serviceCategoryId
) => {
  const servicePart = serviceCategoryId != null ? `svc:${serviceCategoryId}` : "svc:";
  if (item?.id != null) return `${servicePart}|id:${item.id}`;
  if (item?.uuid) return `${servicePart}|uuid:${item.uuid}`;
  if (item?.start_time && item?.end_time) {
    return `${servicePart}|seg:${item.start_time}|${item.end_time}|${
      item.shift_type ?? ""
    }|${resolveBreakdownInvoiceItemId(item) ?? ""}`;
  }
  return `${servicePart}|idx:${index}`;
};

/** Resolve hourly rate + rate amount fields from API breakdown row. */
export const resolveBreakdownRateFields = (item, cataloguePrice, spSettings) => {
  const catalogueVal =
    cataloguePrice ??
    getBreakdownCataloguePrice(item) ??
    item?.item_numbers?.selected?.price ??
    item?.invoice_price ??
    item?.ndis_rate;

  const unitRate = resolvePersistedBreakdownUnitRate({
    ...item,
    catalogue_price: catalogueVal,
    item_numbers: {
      ...(item?.item_numbers || {}),
      selected:
        item?.item_numbers?.selected &&
        typeof item.item_numbers.selected === "object"
          ? item.item_numbers.selected
          : catalogueVal != null && catalogueVal !== ""
            ? { price: catalogueVal }
            : item?.item_numbers?.selected,
    },
  });

  if (breakdownItemUsesAlternateRate(item, cataloguePrice ?? catalogueVal)) {
    return {
      invoiceNdisRate: "alternate",
      rateAmount: unitRate ?? catalogueVal ?? "",
    };
  }

  if (unitRate != null) {
    return {
      invoiceNdisRate: unitRate,
      rateAmount: unitRate,
    };
  }

  if (catalogueVal != null && catalogueVal !== "") {
    return {
      invoiceNdisRate: catalogueVal,
      rateAmount: catalogueVal,
    };
  }

  return { invoiceNdisRate: "", rateAmount: "" };
};

export const breakdownFormHasRateData = (watchFormData, index) => {
  const itemId = watchFormData?.[`invoice_item_id_${index}`];
  const rateAmount = watchFormData?.[`rate_amount_${index}`];
  const ndisRate = watchFormData?.[`invoice_ndis_rate_${index}`];
  const hasItem = itemId != null && itemId !== "";
  const hasRate =
    (rateAmount !== undefined && rateAmount !== null && rateAmount !== "") ||
    (ndisRate !== undefined && ndisRate !== null && ndisRate !== "");
  return hasItem && hasRate;
};

export const clearBreakdownFormFields = (setValue, maxSegments = 12) => {
  for (let i = 0; i < maxSegments; i += 1) {
    setValue(`invoice_item_id_${i}`, undefined);
    setValue(`invoice_ndis_rate_${i}`, undefined);
    setValue(`rate_amount_${i}`, undefined);
    setValue(`invoice_item_name_${i}`, undefined);
    setValue(`applied_rate_${i}`, undefined);
    setValue(`start_time_${i}`, undefined);
    setValue(`end_time_${i}`, undefined);
    setValue(`sleepover_${i}`, undefined);
  }
};
